import React from 'react';
import { ClipboardList, MapPin, ArrowRightLeft, FileText, Hand, RefreshCw, Warehouse, Search, ScanBarcode, ScanLine, Clock, Layers, Users, Boxes, Repeat, PackageSearch, Factory } from 'lucide-react';
import SectionDashboard from '../SectionDashboard';
import { moduleReportTiles } from '@/lib/sectionModules';
import StagingAlert from '@/components/StagingAlert';
import { JoOverdueBanner } from '@/components/processingJo/JoOverdueBanner';
import { JoStuckBanner, STAGE_SETS } from '@/components/processingJo/JoStuckBanner';
import { useNavigate } from 'react-router-dom';

const GreigeStorageDashboard = () => {
  const navigate = useNavigate();

  // Greige Storage-specific operations (6 operations)
  const operations = [
    {
      id: 'bundle-receipt',
      title: 'Bundle Receipt Entry',
      description: 'Scan and receive bundles against documents',
      icon: ClipboardList,
      route: '/sections/greige-storage/bundle-receipt',
      color: 'bg-blue-500'
    },
    {
      id: 'put-away',
      title: 'Location Put Away',
      description: 'Scan bundles and put away to suggested locations',
      icon: MapPin,
      route: '/sections/greige-storage/put-away',
      color: 'bg-green-500'
    },
    {
      id: 'location-transfer',
      title: 'Location to Location Transfer',
      description: 'Transfer bundles between storage locations',
      icon: ArrowRightLeft,
      route: '/sections/greige-storage/location-transfer',
      color: 'bg-cyan-500'
    },
    {
      id: 'pick-slip',
      title: 'Pick Slip Generation',
      description: 'Generate pick slips for accepted shipment requests',
      icon: FileText,
      route: '/sections/greige-storage/pick-slip',
      color: 'bg-purple-500'
    },
    {
      id: 'picking',
      title: 'Picking',
      description: 'Assign pickers and execute picking operations',
      icon: Hand,
      route: '/sections/greige-storage/picking',
      color: 'bg-yellow-600'
    },
    {
      id: 'assortment-pick-slip',
      title: 'Assortment Pick Slip',
      description: 'Build a design + qty colour split, submit for approval → pickable',
      icon: Layers,
      route: '/sections/greige-storage/assortment-pick-slip',
      color: 'bg-indigo-600'
    },
    {
      id: 'assortment-picking',
      title: 'Assortment Picking',
      description: 'Pick by location & colour — no specific barcode needed',
      icon: Hand,
      route: '/sections/greige-storage/assortment-picking',
      color: 'bg-violet-600'
    },
    {
      id: 'tissue-jw',
      title: 'Tissue Job Work Requests',
      description: 'Accept Tissue requests, pick bundles by sort no, Internal Transfer to Tissue',
      icon: Boxes,
      route: '/sections/greige-storage/tissue-jw',
      color: 'bg-emerald-700'
    },
    {
      id: 'processing-jo',
      title: 'Processing Job Orders',
      description: 'Pick base articles for Processing job orders, make pick slips, Internal Transfer to Processing',
      icon: Factory,
      route: '/sections/greige-storage/processing-jo',
      color: 'bg-sky-700'
    },
    {
      id: 'available-pickers',
      title: 'Available Pickers',
      description: "Set today's pickers — approved orders auto-assign to them round-robin",
      icon: Users,
      route: '/sections/greige-storage/available-pickers',
      color: 'bg-teal-600'
    },
    {
      id: 'sort-transfer',
      title: 'Sort No Transfer',
      description: 'Transfer bundles from old sort no to new sort no',
      icon: RefreshCw,
      route: '/sections/greige-storage/sort-transfer',
      color: 'bg-pink-500'
    }
  ];

  // Admin tiles for Location Setup
  const adminTiles = [
    {
      id: 'location-setup',
      title: 'Location Setup',
      description: 'Configure Hall, Floor, Row, Position hierarchy',
      icon: MapPin,
      route: '/sections/greige-storage/location-setup',
      color: 'bg-gray-600'
    }
  ];

  // Utilities
  const utilities = [
    {
      id: 'bundle-usn-trace',
      title: 'Bundle / USN Trace',
      description: 'Universal search — bundle or USN lifecycle report',
      icon: Search,
      route: '/sections/greige-storage/trace',
      color: 'bg-emerald-600'
    },
    {
      id: 'bundle-tracker',
      title: 'Bundle Tracker',
      description: 'Scan a bundle for its full 16-stage lifecycle detail',
      icon: ScanBarcode,
      route: '/sections/greige-storage/bundle-tracker',
      color: 'bg-teal-600'
    },
    {
      id: 'doc-track',
      title: 'Doc Track Report',
      description: 'Enter a document barcode to see each bundle\'s current stage',
      icon: ScanLine,
      route: '/sections/greige-storage/doc-track',
      color: 'bg-sky-600'
    },
    {
      id: 'aging-report',
      title: 'Stock Ageing Report',
      description: 'Stock by age range (0-30 … >365 days, editable) — Greige, SFG & FG',
      icon: Clock,
      route: '/reports/storage-ageing?tab=greige',
      color: 'bg-amber-600'
    },
    {
      id: 'location-stock-report',
      title: 'Location Stock Report',
      description: 'Stored bundles by location, colour & border with CSV export',
      icon: Boxes,
      route: '/sections/greige-storage/location-stock-report',
      color: 'bg-indigo-600'
    },
    {
      id: 'internal-transfer-report',
      title: 'Internal Transfer Report',
      description: 'Printable history of transfers out of Greige Storage — date & universal filters',
      icon: ArrowRightLeft,
      route: '/sections/greige-storage/internal-transfer-report',
      color: 'bg-rose-600'
    },
    {
      id: 'assortment-conversion-report',
      title: 'Assortment Conversion Report',
      description: 'Design conversions from assortment pick slips — from sort → to sort with qty',
      icon: Repeat,
      route: '/sections/greige-storage/assortment-conversion-report',
      color: 'bg-fuchsia-600'
    },
    {
      id: 'picked-not-transferred-report',
      title: 'Pick Slip Status',
      description: 'Picked bundles by pick slip with internal-transfer qty & pending status — return leftovers to storage',
      icon: PackageSearch,
      route: '/sections/greige-storage/picked-not-transferred-report',
      color: 'bg-rose-700'
    },
    {
      id: 'fetch-document-number',
      title: 'Fetch Document Number',
      description: 'Scan a bundle to trace its inward document, gate-in, section receipt & accounts handover',
      icon: Search,
      route: '/reports/fetch-document-number',
      color: 'bg-sky-700'
    }
  ];

  return (
    <SectionDashboard
      sectionName="Greige Storage"
      sectionId="greige-storage"
      sectionKey="Greige Storage"
      moduleId="greige_storage"
      description="Operations Dashboard"
      backRoute="/dashboard"
      operations={operations}
      adminTiles={adminTiles}
      utilities={[...utilities, ...moduleReportTiles('greige-storage', { it: false })]}
      headerIcon={Warehouse}
      alertComponent={
        <div className="space-y-3">
          <StagingAlert 
            section="greige-storage" 
            onNavigate={() => navigate('/sections/greige-storage/bundle-receipt')}
          />
          <JoOverdueBanner onView={() => navigate('/sections/greige-storage/processing-jo')} testId="gs-jo-overdue-banner" />
          <JoStuckBanner stages={STAGE_SETS.greige} testId="gs-jo-stuck-banner" />
        </div>
      }
    />
  );
};

export default GreigeStorageDashboard;
